import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';

import { SwipeDirective } from '../../../../../shared/directives/swipe.directive';
import { CarouselPointerService, PointerSession } from './carousel-pointer.service';
import { CarouselProgressEngine } from './carousel-progress.engine';

export type CarouselSlide = Readonly<{
  key: string;
  backgroundImage: string;
}>;

@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [CommonModule, TranslocoModule, SwipeDirective],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent implements OnInit, OnDestroy {
  private readonly pointerService = inject(CarouselPointerService);

  readonly slides = input<readonly CarouselSlide[]>([
    { key: 'slide_1', backgroundImage: 'images/onboarding/scan_qr_code_feature.png' },
    { key: 'slide_2', backgroundImage: 'images/onboarding/assign_a_location_feature.jpg' },
    { key: 'slide_3', backgroundImage: 'images/onboarding/warranty_extension_feature.jpg' },
    { key: 'slide_4', backgroundImage: 'images/onboarding/white_glowes_assistance.jpg' },
  ]);

  readonly durationMs = input(4500);
  readonly showSkip = input(true);

  readonly completed = output<void>();
  readonly slideChange = output<number>();
  readonly firstSlideHold = output<void>();

  private pointerSession: PointerSession | null = null;
  private firstSlideHoldEmitted = false;

  readonly isPaused = signal(false);
  readonly currentIndex = signal(0);
  readonly progress = signal(0);

  readonly hasSlides = computed(() => this.slides().length > 0);
  readonly lastIndex = computed(() => Math.max(this.slides().length - 1, 0));
  readonly translateX = computed(() => `translateX(-${this.currentIndex() * 100}%)`);

  private progressEngine!: CarouselProgressEngine;

  ngOnInit(): void {
    this.progressEngine = new CarouselProgressEngine(this.durationMs(), {
      getIsPaused: () => this.isPaused(),
      getCurrentProgress: () => this.progress(),
      setProgress: (value) => this.progress.set(value),
      onComplete: () => this.nextSlide(),
    });

    if (!this.hasSlides()) return;

    this.restartProgress();
    this.slideChange.emit(0);
  }

  ngOnDestroy(): void {
    this.progressEngine?.destroy();
  }

  nextSlide(): void {
    if (!this.hasSlides()) return;

    const nextIndex = this.currentIndex() + 1;
    if (nextIndex <= this.lastIndex()) {
      this.setCurrentSlide(nextIndex);
      return;
    }

    this.progressEngine.stop();
    this.progress.set(100);
    this.completed.emit();
  }

  previousSlide(): void {
    if (!this.hasSlides()) return;

    const previousIndex = this.currentIndex() - 1;
    if (previousIndex < 0) return;

    this.setCurrentSlide(previousIndex);
  }

  skip(): void {
    this.progressEngine.stop();
    this.completed.emit();
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.hasSlides()) return;

    this.pointerSession = this.pointerService.start(event);
    if (!this.pointerSession) return;

    this.isPaused.set(true);

    if (this.currentIndex() === 0 && !this.firstSlideHoldEmitted) {
      this.firstSlideHoldEmitted = true;
      this.firstSlideHold.emit();
    }
  }

  onPointerUp(event: PointerEvent): void {
    this.isPaused.set(false);

    const result = this.pointerService.resolveTap(
      event,
      this.pointerSession,
      event.currentTarget as HTMLElement | null,
    );

    this.pointerSession = null;

    if (!result.isTap) return;

    if (result.isLeftZone) {
      this.previousSlide();
    } else {
      this.nextSlide();
    }
  }

  onPointerCancel(): void {
    this.isPaused.set(false);
    this.pointerSession = null;
  }

  getStepProgress(index: number): number {
    const currentIndex = this.currentIndex();

    if (index < currentIndex) return 100;
    if (index > currentIndex) return 0;
    return this.progress();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    const isHidden = document.hidden;
    this.isPaused.set(isHidden);

    if (!isHidden && this.hasSlides()) {
      this.progressEngine.resumeFromCurrent();
    }
  }

  private setCurrentSlide(index: number): void {
    this.currentIndex.set(this.clampIndex(index));
    this.slideChange.emit(this.currentIndex());
    this.restartProgress();
  }

  private restartProgress(): void {
    this.progressEngine.start();
  }

  private clampIndex(index: number): number {
    if (!Number.isFinite(index)) return 0;
    return Math.min(Math.max(index, 0), this.lastIndex());
  }
}
